import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { PeriodComparisonMetric } from '@services/api/dashboard/finance.api'
import {
  getCollectionsDetail,
  type CollectionsOverviewResult,
  getCollectionsOverview,
} from '@services/api/dashboard/overview.api'
import {
  buildComparisonFooter,
  computeChartAxis,
  filterYtdPeriodItems,
  formatCurrency,
  formatGoalCurrencyLabel,
  formatPercent,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import { FINANCE_VS } from '@constants/Dashboard/financeDashboard.constants'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import type { OverviewSectionChartConfig } from '@components/pages/Dashboard/Overview/OverviewMetricSectionCardContent.component'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { useClinicGoal } from './useClinicGoal'

export type UseCollectionsOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type CollectionsDetailKpi = {
  grossCollection: string
  grossGoal: string
  netCollection: string
  netGoal: string
  insuranceCollection: string
  patientCollection: string
  comparisonLabel: string
  grossDelta: string
  grossTrend: 'up' | 'down'
  netDelta: string
  netTrend: 'up' | 'down'
  insuranceDelta: string
  insuranceTrend: 'up' | 'down'
  patientDelta: string
  patientTrend: 'up' | 'down'
}

export type CollectionsSplitChartRow = {
  label: string
  insurance: number
  patient: number
}

export type CollectionsBySourceRow = {
  id: string
  source: string
  collected: string
  adjustments: string
  outstanding: string
  collectionRate: string
}

export type CollectionsByTimePeriodRow = {
  id: string
  timePeriod: string
  grossCollection: string
  netCollection: string
  insurance: string
  patient: string
  collectionRate: string
}

const EMPTY_METRIC: PeriodComparisonMetric = {
  current: 0,
  previous: 0,
  percentChange: 0,
}

function detailTrendDelta(metric: PeriodComparisonMetric): {
  delta: string
  trend: 'up' | 'down'
} {
  const { up } = buildComparisonFooter(metric, { isCurrency: true })
  const pct = Math.abs(metric.percentChange)
  const sign = metric.percentChange >= 0 ? '+' : '-'
  const delta = metric.current - metric.previous
  const amt = formatCurrency(Math.abs(delta), { compact: true })
  return {
    delta: `${sign}${pct.toFixed(1)}% (${amt})`,
    trend: up ? 'up' : 'down',
  }
}

function formatCollectionRate(rate: number): string {
  if (!Number.isFinite(rate) || rate === 0) return HYGIENE_PLACEHOLDER
  return formatPercent(rate, 0)
}

function donutValueFormatter(v: number): string {
  return formatCurrency(v * 1000, { compact: true })
}

export type UseCollectionsResult = {
  loading: boolean
  error: string | null
  hasData: boolean
  /** True once the response has arrived (independent of meaningful content). */
  hasPayload: boolean
  range: RangeKey
  topMetrics: DashboardMetricItemData[]
  highlight: {
    leftLabel: string
    netActual: string
    netGoal: string
  }
  chart: OverviewSectionChartConfig
  splitChart: {
    data: CollectionsSplitChartRow[]
    yAxisDomain: [number, number]
    yAxisTicks: number[]
  }
  kpi: CollectionsDetailKpi
  bySourceRows: CollectionsBySourceRow[]
  byTimePeriodRows: CollectionsByTimePeriodRow[]
}

function useCollectionsFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseCollectionsOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getCollectionsDetail>>
  >
}): UseCollectionsResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )
  const { goal: clinicGoal } = useClinicGoal({ range, date, enabled, queued })

  const summary = data?.summary
  const netGoalStr = formatGoalCurrencyLabel(
    clinicGoal?.gross_collection_goal ?? clinicGoal?.collection_header_goal,
  )
  const grossGoalStr = formatGoalCurrencyLabel(
    clinicGoal?.gross_collection_goal,
  )

  const topMetrics = useMemo((): DashboardMetricItemData[] => {
    if (loading || !summary) {
      return [
        {
          id: 'gross-coll',
          leftLabel: 'Gross collection',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'net-coll',
          leftLabel: 'Net collection',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'ins-coll',
          leftLabel: 'Insurance collection',
          value: HYGIENE_PLACEHOLDER,
        },
      ]
    }
    return [
      {
        id: 'gross-coll',
        leftLabel: 'Gross collection',
        value: formatCurrency(summary.grossCollection.current, {
          compact: true,
        }),
      },
      {
        id: 'net-coll',
        leftLabel: 'Net collection',
        value: formatCurrency(summary.netCollection.current, { compact: true }),
      },
      {
        id: 'ins-coll',
        leftLabel: 'Insurance collection',
        value: formatCurrency(summary.insuranceCollection.current, {
          compact: true,
        }),
      },
    ]
  }, [summary, loading])

  const highlight = useMemo(
    () => ({
      leftLabel: 'Net collection',
      netActual: loading
        ? HYGIENE_PLACEHOLDER
        : formatCurrency(summary?.netCollection.current ?? 0, {
            compact: true,
          }),
      netGoal: netGoalStr
        ? netGoalStr.replace(/^Goal\s*/, '')
        : HYGIENE_PLACEHOLDER,
    }),
    [loading, summary, netGoalStr],
  )

  const chart = useMemo((): OverviewSectionChartConfig => {
    const insurance = (summary?.insuranceCollection.current ?? 0) / 1000
    const patient = (summary?.patientCollection.current ?? 0) / 1000
    return {
      title: 'Insurance vs Patient split',
      primaryValue: loading ? 0 : insurance,
      secondaryValue: loading ? 0 : patient,
      valueFormatter: donutValueFormatter,
      slices: [
        { name: 'Insurance', value: insurance, color: 'var(--blue-400)' },
        { name: 'Patient', value: patient, color: 'var(--orange-300)' },
      ],
    }
  }, [summary, loading])

  const splitChart = useMemo(() => {
    const insuranceBuckets = filterYtdPeriodItems(
      range,
      date,
      data?.insuranceCollectionPeriodSummary ?? [],
      (b) => b.label,
    )
    const patientBuckets = filterYtdPeriodItems(
      range,
      date,
      data?.patientCollectionPeriodSummary ?? [],
      (b) => b.label,
    )
    const chartData: CollectionsSplitChartRow[] = insuranceBuckets.map(
      (ins, i) => ({
        label: ins.label,
        insurance: ins.value / 1000,
        patient: (patientBuckets[i]?.value ?? 0) / 1000,
      }),
    )
    const values = chartData.flatMap((p) => [p.insurance, p.patient])
    const axis = computeChartAxis(values)
    return {
      data: chartData,
      yAxisDomain: axis.yDomain,
      yAxisTicks: axis.yTicks,
    }
  }, [data, range, date])

  const kpi = useMemo((): CollectionsDetailKpi => {
    const s: CollectionsOverviewResult['summary'] = summary ?? {
      grossCollection: EMPTY_METRIC,
      netCollection: EMPTY_METRIC,
      insuranceCollection: EMPTY_METRIC,
      patientCollection: EMPTY_METRIC,
    }
    const grossTrend = detailTrendDelta(s.grossCollection)
    const netTrend = detailTrendDelta(s.netCollection)
    const insTrend = detailTrendDelta(s.insuranceCollection)
    const patTrend = detailTrendDelta(s.patientCollection)
    return {
      grossCollection: formatCurrency(s.grossCollection.current, {
        compact: true,
      }),
      grossGoal: grossGoalStr,
      netCollection: formatCurrency(s.netCollection.current, { compact: true }),
      netGoal: netGoalStr,
      insuranceCollection: formatCurrency(s.insuranceCollection.current, {
        compact: true,
      }),
      patientCollection: formatCurrency(s.patientCollection.current, {
        compact: true,
      }),
      comparisonLabel: FINANCE_VS[range],
      grossDelta: grossTrend.delta,
      grossTrend: grossTrend.trend,
      netDelta: netTrend.delta,
      netTrend: netTrend.trend,
      insuranceDelta: insTrend.delta,
      insuranceTrend: insTrend.trend,
      patientDelta: patTrend.delta,
      patientTrend: patTrend.trend,
    }
  }, [summary, range, grossGoalStr, netGoalStr])

  const bySourceRows = useMemo((): CollectionsBySourceRow[] => {
    return (data?.bySource ?? []).map((row, i) => ({
      id: String(i + 1),
      source: row.source,
      collected: formatCurrency(row.collected, { compact: true }),
      adjustments: formatCurrency(row.adjustment, { compact: true }),
      outstanding: formatCurrency(row.outstanding, { compact: true }),
      collectionRate: formatCollectionRate(row.collectionRate),
    }))
  }, [data])

  const byTimePeriodRows = useMemo((): CollectionsByTimePeriodRow[] => {
    const rows = filterYtdPeriodItems(
      range,
      date,
      data?.byTimePeriod ?? [],
      (r) => r.period,
    )
    return rows.map((row, i) => ({
      id: String(i + 1),
      timePeriod: row.period,
      grossCollection: formatCurrency(row.gross, { compact: true }),
      netCollection: formatCurrency(row.net, { compact: true }),
      insurance: formatCurrency(row.insurance, { compact: true }),
      patient: formatCurrency(row.patient, { compact: true }),
      collectionRate: formatCollectionRate(row.collectionRate),
    }))
  }, [data, range, date])

  return {
    loading,
    error,
    hasData: hasNonZeroDashboardValues(
      data?.summary.grossCollection.current,
      data?.summary.netCollection.current,
      data?.summary.insuranceCollection.current,
      data?.summary.patientCollection.current,
    ),
    hasPayload: data != null,
    range,
    topMetrics,
    highlight,
    chart,
    splitChart,
    kpi,
    bySourceRows,
    byTimePeriodRows,
  }
}

export function useCollections(
  options: UseCollectionsOptions,
): UseCollectionsResult {
  return useCollectionsFromFetcher({
    ...options,
    fetcher: getCollectionsDetail,
  })
}

export function useCollectionsOverview(
  options: UseCollectionsOptions,
): UseCollectionsResult {
  return useCollectionsFromFetcher({
    ...options,
    fetcher: getCollectionsOverview,
  })
}
